import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Navigate, useLocation, useParams } from "react-router-dom";
import useUserStore from "@store/useUserStore";
import { TokenManager } from "utils/api";
import {
    EmptyConversation,
    MessageBubble,
    MessageInputBar,
    MessageRoomHeader,
} from "./MessageComponents";
import {
    getMessageRoom,
    markMessageRoomRead,
    groupMessagesByDay,
    mapChatMessageToViewMessage,
    sendMessage,
    sendMessageWithAttachments,
    uploadAttachment,
} from "api/chat";
import { createChatSocket } from "api/chatSocket";
import useGoBack from "hooks/useGoBack";
import { MessageRoomPeerSheets, PeerMenuButton, isPeerActionable } from "./MessageRoomPeerActions";
import { readCachedRoom, removeCachedRoom, writeCachedRoom } from "./roomCache";
import "./Messages.scss";

// 채팅방 본문은 .message-room__body 이지만, 앱 전체가 PullToRefresh(.ptr-scroll-container,
// height:100%; overflow-y:auto)로 감싸여 있어 실제 스크롤 주인은 본문이 아니라 조상
// .ptr-scroll-container 인 경우가 많다. 본문에 scrollTop 을 줘도 화면이 움직이지 않으므로,
// "실제로 스크롤 가능한" 컨테이너를 안전하게 찾아서 거기에 바닥 고정/관찰을 적용한다.
export const NEAR_BOTTOM_THRESHOLD = 96;

export function resolveScrollContainer(bodyEl) {
    const fallback = () =>
        typeof document !== "undefined" ? document.querySelector(".ptr-scroll-container") : null;
    if (!bodyEl) return fallback();
    const canScroll = (el) => Boolean(el) && el.scrollHeight - el.clientHeight > 1;
    // 본문 자체가 실제 스크롤 컨테이너면 그대로 사용한다(레이아웃에 따라 본문이 스크롤될 수도 있음).
    if (canScroll(bodyEl)) return bodyEl;
    // 아니면 가장 가까운 .ptr-scroll-container 조상, 그것도 없으면 문서 내 컨테이너로 폴백.
    const ancestor =
        (typeof bodyEl.closest === "function" ? bodyEl.closest(".ptr-scroll-container") : null) ||
        fallback();
    return ancestor || bodyEl;
}

export function scrollElementToBottom(el, behavior = "auto") {
    if (!el) return;
    // scrollTo 가 무시되거나 지원되지 않는 환경(일부 웹뷰)을 대비해 scrollTop 도 항상 강제 적용한다.
    if (typeof el.scrollTo === "function") {
        try {
            el.scrollTo({ top: el.scrollHeight, behavior });
        } catch {
            try {
                el.scrollTo(0, el.scrollHeight);
            } catch {
                // 일부 JSDOM/WebView 환경은 scrollTo 자체가 throw 될 수 있다.
                // 아래 scrollTop 직접 대입이 최종 fallback 이므로 여기서는 무시한다.
            }
        }
    }
    el.scrollTop = el.scrollHeight;
}

export function isNearBottom(el, threshold = NEAR_BOTTOM_THRESHOLD) {
    if (!el) return true;
    return el.scrollHeight - el.scrollTop - el.clientHeight < threshold;
}

// scrollToBottom 은 즉시 1회 + rAF + 짧은 timeout 으로 바닥 고정을 재시도한다. 이때 사용자가
// (특히 첨부 패널을 연 직후) 곧바로 위로 스크롤하면, 강제 스크롤이 아닌 지연 재시도가 그 동작을
// 되돌려선 안 된다. force=true(초기 진입/내가 보낸 메시지)는 webview 레이아웃 지연을 이겨내기 위해
// 위치와 무관하게 고정하고, 그 외에는 여전히 바닥 근처일 때만 다시 고정한다.
export function shouldStickOnRetry(container, force = false) {
    return Boolean(force) || isNearBottom(container);
}

// 모바일 키보드가 화면을 덮는 높이(px). iOS 는 키보드가 열려도 layout viewport 가 줄지 않아
// window.innerHeight 와 visualViewport.height 의 차이가 곧 키보드 높이가 되고, 페이지가
// 위로 팬 된 경우 offsetTop 만큼은 이미 가려지지 않으므로 빼서 보정한다.
// Android 처럼 layout viewport 자체가 줄어드는 환경에서는 차이가 0 에 수렴해 이중 보정이 없다.
export function getKeyboardInset(viewport, windowHeight) {
    if (!viewport || typeof windowHeight !== "number") return 0;
    const height = typeof viewport.height === "number" ? viewport.height : windowHeight;
    const offsetTop = typeof viewport.offsetTop === "number" ? viewport.offsetTop : 0;
    return Math.max(0, Math.round(windowHeight - height - offsetTop));
}

function MessageRoom() {
    const { messageRoomId } = useParams();
    const location = useLocation();
    // 채팅방 Back: 이전 화면이 있으면 이전 화면, 없으면 홈(/) replace. 푸시 cold-start 든 레이어 직접 오픈이든
    // 구분하지 않는다. 목록 history 를 강제로 만들지 않고, 다른 화면의 useGoBack 기본값은 건드리지 않는다.
    const goBack = useGoBack({ fallbackToHome: true });
    const userInfo = useUserStore((state) => state.userInfo);
    const hasAccessToken = Boolean(TokenManager.getAccessToken());
    const currentUserId = String(userInfo?.user_id ?? "");
    const routeStateRoom = React.useMemo(() => {
        const stateUserId = String(location.state?.cacheUserId ?? "");
        if (!currentUserId || stateUserId !== currentUserId) return null;
        return location.state?.room && String(location.state.room.id) === String(messageRoomId)
            ? location.state.room
            : null;
    }, [location.state, messageRoomId, currentUserId]);
    // 로드 effect 는 route-state room 을 **객체 신원이 아니라 ref 로** 읽는다. 브라우저는 history traversal
    // (예: MediaViewer 가 pushState/back 으로 열고 닫음) 때마다 route-state 를 역직렬화한 새 객체로 돌려주는데,
    // 같은 계정·같은 방인데도 그 신원 변화만으로 messages 를 비우고 다시 불러오면 첨부 목록(과 그 저장 상태)이
    // 언마운트된다. 실제 방/계정/토큰 전환은 아래 effect 의 나머지 deps 가 그대로 리셋한다.
    const routeStateRoomRef = useRef(routeStateRoom);
    useEffect(() => {
        routeStateRoomRef.current = routeStateRoom;
    }, [routeStateRoom]);
    const [room, setRoom] = useState(() => routeStateRoom);
    const [isLoading, setIsLoading] = useState(() => !routeStateRoom);
    const [errorMessage, setErrorMessage] = useState("");
    const [isSending, setIsSending] = useState(false);
    const inputRef = useRef(null);
    const bodyRef = useRef(null);
    const roomSectionRef = useRef(null);
    const bottomAnchorRef = useRef(null);
    const shouldStickToBottomRef = useRef(true);
    const forceScrollRef = useRef(false);
    const inputFocusedRef = useRef(false);
    const focusRetryTimersRef = useRef([]);
    const viewportHeightRef = useRef(null);
    const hasMessages = room?.messages && room.messages.length > 0;
    const timelineMessages = hasMessages ? groupMessagesByDay(room.messages) : [];
    // 신고/차단 액션: 트리거 버튼은 header 안에, BottomSheet 본체는 header 바깥에서 렌더한다.
    // (header 의 backdrop-filter 가 만든 containing block 에 fixed overlay 가 갇히는 문제 회피)
    // 메뉴 열림 상태만 부모에서 들고, community_dm 일 때만 시트 컴포넌트를 mount 한다.
    const [peerMenuOpen, setPeerMenuOpen] = useState(false);
    const peerActionable = isPeerActionable(room);
    // 하단 이동 버튼 노출 상태. near-bottom 추적 자체는 기존 ref 로 유지하고,
    // 버튼 렌더에 필요한 값만 state 로 미러링한다(값이 임계를 넘을 때만 리렌더).
    const [isAwayFromBottom, setIsAwayFromBottom] = useState(false);
    const [hasUnseenMessage, setHasUnseenMessage] = useState(false);

    // 방 전환(param 변경, remount 없음): 이전 방의 per-room 상태를 커밋 전(render 단계)에 즉시 초기화한다.
    // 데이터 로드 effect 에서 리셋하면 이전 방의 하단 이동 버튼이 한 프레임 그려진 뒤 사라져 "버튼 잔상"이
    // 남는다. React 공식 "이전 렌더 정보로 상태 조정" 패턴 — sentinel 은 반드시 state 여야 한다.
    // ref sentinel 은 폐기된 렌더(StrictMode 이중 호출, concurrent 중단)에서 롤백되지 않아 상태 리셋만
    // 유실될 수 있다. 조건부 setState 라 무한 루프가 없고, 값이 이미 초기값이면 React 가 bail-out 한다.
    const [trackedRoomId, setTrackedRoomId] = useState(messageRoomId);
    if (trackedRoomId !== messageRoomId) {
        setTrackedRoomId(messageRoomId);
        setIsAwayFromBottom(false);
        setHasUnseenMessage(false);
    }

    // scroll-follow 참조 초기화는 render 중 ref 변경(비순수·비롤백) 대신 layout effect 에서 수행한다.
    // ref 는 페인트 출력에 쓰이지 않아 커밋 직후 갱신이면 충분하고, 새 방 메시지가 로드되는 커밋의
    // scroll effect 가 forceScrollRef 를 읽어 최신 하단 고정을 보장한다.
    useLayoutEffect(() => {
        shouldStickToBottomRef.current = true;
        forceScrollRef.current = true;
    }, [messageRoomId]);

    const markCurrentRoomRead = useCallback(() => {
        if (!messageRoomId) return;
        markMessageRoomRead(messageRoomId).catch(() => undefined);
    }, [messageRoomId]);

    const scrollToBottom = useCallback((behavior = "auto", { force = false } = {}) => {
        // 모바일/웹뷰에서는 scrollTo 가 무시되거나 레이아웃이 덜 잡혀 바닥 고정이 실패할 수 있다.
        // 매 시도마다 실제 스크롤 컨테이너를 다시 해석해(렌더 직후엔 본문이 아직 미스크롤일 수 있음)
        // scrollTo + scrollTop 을 함께 적용하고, rAF 와 짧은 timeout 으로 한 번씩 더 재시도한다.
        // 단, 예약된 rAF/timeout 이 unmount 이후 실행되면 다른 화면의 전역 .ptr-scroll-container 를
        // 건드리면 안 되므로 bodyRef 가 없으면 no-op 처리한다.
        // bottomAnchor.scrollIntoView 만으로는 입력바 높이만큼 diff(약 92px)가 남아 최신 메시지가 가려질 수 있다.
        // 따라서 anchor 정렬을 먼저 수행하고, 마지막엔 항상 실제 스크롤 컨테이너를 scrollTop=scrollHeight 로 고정해 diff=0 으로 끝낸다.
        const stick = () => {
            const bodyEl = bodyRef.current;
            if (!bodyEl) return;
            const container = resolveScrollContainer(bodyEl);
            bottomAnchorRef.current?.scrollIntoView?.({ block: "end", behavior });
            scrollElementToBottom(container, behavior);
        };
        // 지연 재시도는 그 사이 사용자가 위로 스크롤했다면(강제 스크롤이 아닌 한) 되돌리지 않는다.
        const retryStick = () => {
            const bodyEl = bodyRef.current;
            if (!bodyEl) return;
            const container = resolveScrollContainer(bodyEl);
            if (!shouldStickOnRetry(container, force)) return;
            bottomAnchorRef.current?.scrollIntoView?.({ block: "end", behavior });
            scrollElementToBottom(container, behavior);
        };
        stick();
        window.requestAnimationFrame(retryStick);
        window.setTimeout(retryStick, 60);
    }, []);

    // 입력창 focus(=모바일 키보드 오픈) 시에는 최신 메시지 노출이 우선이므로 위치와 무관하게
    // 바닥으로 강제 고정한다. 키보드 애니메이션으로 visualViewport 높이가 늦게 바뀌는
    // iOS/Android WebView 를 대비해 짧은 지연 retry 도 함께 둔다(아래 visualViewport effect 와 보완).
    const clearFocusRetryTimers = useCallback(() => {
        focusRetryTimersRef.current.forEach((id) => window.clearTimeout(id));
        focusRetryTimersRef.current = [];
    }, []);

    const handleInputFocus = useCallback(() => {
        inputFocusedRef.current = true;
        viewportHeightRef.current = typeof window !== "undefined" ? window.visualViewport?.height ?? null : null;
        shouldStickToBottomRef.current = true;
        if (hasUnseenMessage) markCurrentRoomRead();
        setIsAwayFromBottom(false);
        setHasUnseenMessage(false);
        scrollToBottom("auto", { force: true });
        clearFocusRetryTimers();
        focusRetryTimersRef.current = [150, 300].map((delay) =>
            window.setTimeout(() => {
                if (inputFocusedRef.current) scrollToBottom("auto", { force: true });
            }, delay)
        );
    }, [scrollToBottom, clearFocusRetryTimers, hasUnseenMessage, markCurrentRoomRead]);

    const handleInputBlur = useCallback(() => {
        inputFocusedRef.current = false;
        viewportHeightRef.current = null;
        clearFocusRetryTimers();
    }, [clearFocusRetryTimers]);

    // unmount 시 예약된 재시도 타이머를 정리해, 다른 화면의 스크롤 컨테이너를 건드리지 않게 한다.
    // (effect 본체는 아무것도 하지 않고 cleanup 으로 타이머 정리만 등록한다)
    useEffect(() => {
        return clearFocusRetryTimers;
    }, [clearFocusRetryTimers]);

    const updateStickToBottom = useCallback(() => {
        const element = resolveScrollContainer(bodyRef.current);
        const nearBottom = isNearBottom(element);
        shouldStickToBottomRef.current = nearBottom;
        return nearBottom;
    }, []);

    // 스크롤 이벤트 전용: near-bottom ref 갱신에 더해 하단 이동 버튼 노출 state 를 동기화한다.
    // (updateStickToBottom 은 setRoom updater 안에서도 불리므로 setState 를 섞지 않는다)
    const syncBottomState = useCallback(() => {
        const nearBottom = updateStickToBottom();
        setIsAwayFromBottom(!nearBottom);
        if (nearBottom) {
            if (hasUnseenMessage) markCurrentRoomRead();
            setHasUnseenMessage(false);
        }
    }, [hasUnseenMessage, markCurrentRoomRead, updateStickToBottom]);

    useEffect(() => {
        if (!hasMessages) return;
        if (!forceScrollRef.current && !shouldStickToBottomRef.current) return;
        const force = forceScrollRef.current;
        forceScrollRef.current = false;
        setIsAwayFromBottom(false);
        setHasUnseenMessage(false);
        window.requestAnimationFrame(() => scrollToBottom("auto", { force }));
        // room?.id: 방 전환에서 A/B 메시지 수가 같고 이미 settle 된 응답이 배칭돼 중간 empty 커밋이
        // 생략되면 hasMessages/length 만으로는 재발화하지 않는다 — room identity 로 항상 재평가한다.
    }, [hasMessages, room?.id, room?.messages?.length, scrollToBottom]);

    // 실제 스크롤 주인이 본문이 아니라 조상 .ptr-scroll-container 일 수 있어, 본문의 onScroll 만으로는
    // near-bottom 추적이 동작하지 않는다. 해석된 컨테이너에 직접 scroll 리스너를 붙여 사용자가
    // 과거 메시지를 읽는 중인지(=바닥에서 멀어졌는지)를 정확히 추적한다.
    useEffect(() => {
        if (!hasMessages) return undefined;
        const container = resolveScrollContainer(bodyRef.current);
        if (!container || typeof container.addEventListener !== "function") return undefined;
        const handleScroll = () => syncBottomState();
        container.addEventListener("scroll", handleScroll, { passive: true });
        return () => container.removeEventListener("scroll", handleScroll);
    }, [hasMessages, messageRoomId, syncBottomState]);

    // 모바일 키보드가 열릴 때 window.visualViewport 높이가 줄어든다. 입력창이 focus 된
    // 동안에도 키보드 오픈(height 감소)으로 판단될 때만 다시 바닥으로 강제 고정한다.
    // focus 가 아니거나 키보드 닫힘/회전처럼 height 가 증가·유지되는 resize 는 건드리지 않는다.
    useEffect(() => {
        const viewport = typeof window !== "undefined" ? window.visualViewport : null;
        if (!viewport || typeof viewport.addEventListener !== "function") return undefined;
        // 키보드가 덮는 높이를 CSS 변수로 주입해 본문 하단 padding/입력바 위치를 보정한다.
        // 키보드 애니메이션 동안 resize 가 연속 발생하므로 리렌더 없이 DOM 스타일만 갱신한다.
        const applyKeyboardInset = () => {
            const inset = getKeyboardInset(viewport, window.innerHeight);
            roomSectionRef.current?.style?.setProperty("--message-keyboard-inset", `${inset}px`);
        };
        const handleViewportResize = () => {
            applyKeyboardInset();
            if (!inputFocusedRef.current) return;
            const previousHeight = viewportHeightRef.current;
            const nextHeight = viewport.height ?? null;
            viewportHeightRef.current = nextHeight;
            if (previousHeight != null && nextHeight != null && nextHeight >= previousHeight) return;
            scrollToBottom("auto", { force: true });
        };
        applyKeyboardInset();
        viewport.addEventListener("resize", handleViewportResize);
        // 일부 iOS/WebView 는 키보드 팬/안정화 과정에서 height 변화 없이 offsetTop 만 바뀌며
        // visualViewport.scroll 로 통지한다. 같은 핸들러로 CSS inset 을 즉시 재계산한다.
        viewport.addEventListener("scroll", handleViewportResize);
        return () => {
            viewport.removeEventListener("resize", handleViewportResize);
            viewport.removeEventListener("scroll", handleViewportResize);
        };
    }, [scrollToBottom]);

    // 이미지·영상은 로드 완료 시점에 높이가 늘어 본문 스크롤 위치가 위로 밀린다.
    // 사용자가 하단(near-bottom)에 머물러 있을 때만 다시 바닥으로 고정해, 과거 메시지를
    // 읽고 있는 중에는 화면이 튀지 않게 한다.
    const handleMediaLoad = useCallback(() => {
        if (!shouldStickToBottomRef.current) return;
        window.requestAnimationFrame(() => scrollToBottom("auto"));
    }, [scrollToBottom]);

    const appendMessage = useCallback((created, { forceScroll = false } = {}) => {
        const viewMessage = mapChatMessageToViewMessage(created, currentUserId);
        setRoom((currentRoom) => {
            if (!currentRoom) return currentRoom;
            const exists = (currentRoom.messages || []).some((message) => {
                const rawClientId = message.raw?.clientMessageId ?? message.raw?.client_message_id;
                const nextClientId = created.clientMessageId ?? created.client_message_id;
                return message.id === viewMessage.id || (rawClientId && nextClientId && rawClientId === nextClientId);
            });
            if (exists) {
                if (forceScroll) {
                    shouldStickToBottomRef.current = true;
                    window.requestAnimationFrame(() => scrollToBottom("auto", { force: true }));
                }
                return currentRoom;
            }
            if (forceScroll) {
                forceScrollRef.current = true;
                shouldStickToBottomRef.current = true;
            } else {
                updateStickToBottom();
            }
            return {
                ...currentRoom,
                lastMessage: created.body || (viewMessage.attachments?.length ? "첨부파일을 보냈습니다." : currentRoom.lastMessage),
                messages: [
                    ...(currentRoom.messages || []),
                    viewMessage,
                ],
            };
        });
    }, [currentUserId, scrollToBottom, updateStickToBottom]);

    useEffect(() => {
        if (!userInfo || !hasAccessToken || !messageRoomId) return undefined;
        let isMounted = true;
        const cachedRoom = routeStateRoomRef.current || readCachedRoom(currentUserId, messageRoomId);
        if (cachedRoom && String(cachedRoom.id) === String(messageRoomId)) {
            setRoom({
                ...cachedRoom,
                messages: [],
            });
        } else {
            setRoom(null);
        }
        setIsLoading(!cachedRoom);
        setErrorMessage("");
        // per-room 상태 초기화는 render 단계(trackedRoomId sentinel)와 layout effect(scroll-follow ref)
        // 에서 커밋 전후로 이미 수행한다 — 여기서 다시 리셋하지 않는다.

        getMessageRoom(messageRoomId, userInfo)
            .then((nextRoom) => {
                if (!isMounted) return;
                setRoom((currentRoom) => ({
                    ...currentRoom,
                    ...nextRoom,
                    messages: nextRoom.messages || [],
                }));
                forceScrollRef.current = true;
                shouldStickToBottomRef.current = true;
                writeCachedRoom(currentUserId, nextRoom);
                markCurrentRoomRead();
            })
            .catch((error) => {
                if (!isMounted) return;
                setRoom(null);
                if (error?.response?.status === 403 || error?.response?.status === 404) {
                    removeCachedRoom(currentUserId, messageRoomId);
                    setErrorMessage("접근할 수 없거나 존재하지 않는 메시지방입니다.");
                    return;
                }
                setErrorMessage("메시지방을 불러오지 못했습니다. 잠시 후 다시 시도해주세요.");
            })
            .finally(() => {
                if (isMounted) setIsLoading(false);
            });

        return () => {
            isMounted = false;
        };
    }, [messageRoomId, userInfo, hasAccessToken, currentUserId, markCurrentRoomRead]);

    useEffect(() => {
        if (!userInfo || !hasAccessToken || !messageRoomId) return undefined;
        const socket = createChatSocket();
        if (!socket) return undefined;

        const handleCreated = (message) => {
            const roomId = String(message?.roomId ?? message?.room_id ?? "");
            if (roomId !== String(messageRoomId)) return;
            // 내 메시지(다른 기기/socket echo)와 입력창 focus(키보드 열림) 중에는 항상 따라가고,
            // 그 외에는 바닥 근처일 때만 따라간다. 과거 메시지를 읽는 중이면 강제 스크롤 대신
            // "새 메시지" 하단 이동 버튼을 노출한다.
            const senderId = String(message?.senderUserId ?? message?.sender_user_id ?? "");
            const isMine = Boolean(currentUserId) && senderId === currentUserId;
            const wasNearBottom = isNearBottom(resolveScrollContainer(bodyRef.current));
            const shouldFollow = isMine || wasNearBottom || inputFocusedRef.current;
            appendMessage(message, { forceScroll: shouldFollow });
            if (!shouldFollow) {
                setIsAwayFromBottom(true);
                setHasUnseenMessage(true);
                return;
            }
            markCurrentRoomRead();
        };

        socket.on("connect", () => {
            socket.emit("room:join", { roomId: messageRoomId });
        });
        socket.on("message:created", handleCreated);
        socket.on("connect_error", () => undefined);

        return () => {
            socket.off("message:created", handleCreated);
            socket.disconnect();
        };
    }, [appendMessage, currentUserId, hasAccessToken, markCurrentRoomRead, messageRoomId, userInfo]);

    // 하단 이동 버튼: 과거 메시지를 보다가 한 번에 최신으로 복귀한다.
    const handleJumpToLatest = useCallback(() => {
        setIsAwayFromBottom(false);
        setHasUnseenMessage(false);
        shouldStickToBottomRef.current = true;
        scrollToBottom("auto", { force: true });
        markCurrentRoomRead();
    }, [markCurrentRoomRead, scrollToBottom]);

    const handleUploadAttachment = (file, options) =>
        uploadAttachment({ roomId: room.id, file, ...options });

    const handleSendMessage = async ({ body, attachmentIds = [] } = {}) => {
        if (!room || isSending) return;
        const hasAttachments = attachmentIds.length > 0;
        const trimmed = String(body || "").trim();
        if (!trimmed && !hasAttachments) return;
        setIsSending(true);
        setErrorMessage("");
        try {
            const created = hasAttachments
                ? await sendMessageWithAttachments(room.id, { body: trimmed, attachmentIds })
                : await sendMessage(room.id, trimmed);
            if (!created) return false;
            appendMessage(created, { forceScroll: true });
            return true;
        } catch {
            setErrorMessage("메시지를 보내지 못했습니다. 잠시 후 다시 시도해주세요.");
            return false;
        } finally {
            setIsSending(false);
            window.requestAnimationFrame(() => inputRef.current?.focus());
        }
    };

    if (!userInfo || !hasAccessToken) {
        return <Navigate to="/login" replace />;
    }

    return (
        <main className="messages-page messages-page--room">
            <section className="message-room" aria-labelledby="message-room-title" ref={roomSectionRef}>
                {room ? (
                    <MessageRoomHeader
                        room={room}
                        onBack={goBack}
                        peerActionsTrigger={
                            peerActionable ? (
                                <PeerMenuButton open={peerMenuOpen} onOpen={() => setPeerMenuOpen(true)} />
                            ) : null
                        }
                    />
                ) : null}
                <div className="message-room__body" ref={bodyRef} onScroll={syncBottomState}>
                    {isLoading && <p className="message-state">메시지방을 불러오는 중입니다.</p>}
                    {errorMessage && !isLoading && <p className="message-state message-state--error">{errorMessage}</p>}

                    {room && !isLoading && (
                        hasMessages ? (
                            timelineMessages.map((message) => (
                                <MessageBubble key={message.id} message={message} opponentAvatar={room.avatar} peer={room.peer} onMediaLoad={handleMediaLoad} />
                            ))
                        ) : (
                            <EmptyConversation room={room} />
                        )
                    )}
                    <div ref={bottomAnchorRef} className="message-room__bottom-anchor" aria-hidden="true" />
                </div>
                {room && !isLoading && hasMessages && isAwayFromBottom && (
                    <button
                        type="button"
                        className={`message-room__jump${hasUnseenMessage ? " message-room__jump--unseen" : ""}`}
                        onClick={handleJumpToLatest}
                        // 키보드가 열린 채 탭해도 입력창 focus 를 뺏지 않아 키보드가 닫히지 않게 한다.
                        // (전송 버튼과 동일하게 pointer/mouse 둘 다 막는다 — iOS 는 pointerdown 이 먼저 blur 를 유발)
                        onPointerDown={(event) => event.preventDefault()}
                        onMouseDown={(event) => event.preventDefault()}
                    >
                        {hasUnseenMessage ? "새 메시지" : "최신 메시지"}
                        <span aria-hidden="true">↓</span>
                    </button>
                )}
                {room && (
                    <MessageInputBar
                        disabled={false}
                        sending={isSending}
                        inputRef={inputRef}
                        onSend={handleSendMessage}
                        onUploadAttachment={handleUploadAttachment}
                        onFocus={handleInputFocus}
                        onBlur={handleInputBlur}
                    />
                )}
            </section>
            {/* 신고/차단 BottomSheet 는 header 의 backdrop-filter 영향을 받지 않도록
                header 바깥(.messages-page 레벨)에서 렌더한다. community_dm 일 때만 mount. */}
            {room && peerActionable && (
                <MessageRoomPeerSheets
                    roomId={room.id}
                    peer={room.peer}
                    menuOpen={peerMenuOpen}
                    onMenuClose={() => setPeerMenuOpen(false)}
                />
            )}
        </main>
    );
}

export default MessageRoom;
